import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import type { Campaign } from '@/types'

const QK = ['campaigns'] as const

export function useCampaigns() {
  const qc = useQueryClient()

  const { data: campaigns = [], isPending, isError, refetch } = useQuery({
    queryKey: QK,
    queryFn: async () => {
      // A02: limit defensivo. Tenant com 100k campanhas seria patológico — antes
      // disso já tem outro problema. Hoje T-Connected tem 4. Margem ampla.
      const { data, error } = await supabase.from('campaigns').select('*').order('start_date', { ascending: false }).limit(500)
      if (error) throw error
      return (data || []) as Campaign[]
    },
  })

  const createM = useMutation({
    mutationFn: async (campaign: Partial<Campaign>) => {
      const { data, error } = await supabase.from('campaigns').insert(campaign).select().single()
      if (error) throw error
      return data as Campaign
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: QK }); toast.success('Campanha criada com sucesso') },
    onError: () => { toast.error('Erro ao criar campanha') },
  })

  const updateM = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Campaign> }) => {
      const { data, error } = await supabase.from('campaigns').update(updates).eq('id', id).select().single()
      if (error) throw error
      return data as Campaign
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: QK }); toast.success('Campanha atualizada') },
    onError: () => { toast.error('Erro ao atualizar campanha') },
  })

  const removeM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('campaigns').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: QK }); toast.success('Campanha removida') },
    onError: () => { toast.error('Erro ao remover campanha') },
  })

  const create = async (campaign: Partial<Campaign>) => {
    try { const data = await createM.mutateAsync(campaign); return { data, error: null } }
    catch (error) { return { data: null, error } }
  }
  const update = async (id: string, updates: Partial<Campaign>) => {
    try { const data = await updateM.mutateAsync({ id, updates }); return { data, error: null } }
    catch (error) { return { data: null, error } }
  }
  const remove = async (id: string) => {
    try { await removeM.mutateAsync(id); return { error: null } }
    catch (error) { return { error } }
  }

  return { campaigns, loading: isPending, isError, refetch, create, update, remove }
}
